import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  History, Search, RefreshCw, Eye, Receipt, Filter, Calendar,
  Banknote, CreditCard, Smartphone, RotateCcw, ArrowLeftRight, FileDown, Printer, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { formatDate, todayIST, shiftDateStr } from '@/utils/dateFormat';
import { posAPI, storesAPI, storeSyncAPI, businessSettingsAPI } from '@/services/api';
import html2pdf from 'html2pdf.js';
import A4GSTInvoice from '@/components/A4GSTInvoice';
import ReceiptComponent from '@/components/Receipt';

const SalesHistoryPage = () => {
  const [activeTab, setActiveTab] = useState('sales');
  const [sales, setSales] = useState([]);
  const [returns, setReturns] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');
  const [summary, setSummary] = useState(null);
  const [voiding, setVoiding] = useState(false);
  
  // View dialogs
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewType, setViewType] = useState('sale'); // 'sale', 'return', 'exchange'
  
  // Store info and GST settings for reprint
  const [storeInfo, setStoreInfo] = useState(null);
  const [gstInvoiceSettings, setGstInvoiceSettings] = useState(null);
  const a4InvoiceRef = useRef(null);
  const receiptRef = useRef(null);
  
  const { user } = useAuth();
  const { toast } = useToast();
  const storeId = user?.entity_id;

  const getDateRange = (filter) => {
    const todayStr = todayIST();
    
    switch (filter) {
      case 'today':
        return { from_date: todayStr, to_date: todayStr };
      case 'yesterday': {
        const yStr = shiftDateStr(todayStr, -1);
        return { from_date: yStr, to_date: yStr };
      }
      case 'week':
        return { from_date: shiftDateStr(todayStr, -7), to_date: todayStr };
      case 'this_month':
        return { from_date: `${todayStr.slice(0, 8)}01`, to_date: todayStr };
      case 'last_month': {
        const lastPrev = shiftDateStr(`${todayStr.slice(0, 8)}01`, -1);
        return { from_date: `${lastPrev.slice(0, 8)}01`, to_date: lastPrev };
      }
      case 'month':
        return { from_date: shiftDateStr(todayStr, -30), to_date: todayStr };
      default:
        return {};
    }
  };

  const fetchData = useCallback(async () => {
    if (!storeId) return;
    
    setLoading(true);
    try {
      const dateRange = getDateRange(dateFilter);
      const params = {
        store_id: storeId,
        ...dateRange,
        search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        limit: 1000,
      };
      
      // Fetch all data in parallel
      const [salesRes, returnsRes, exchangesRes, summaryRes] = await Promise.all([
        posAPI.listSales(params),
        posAPI.listReturns({ store_id: storeId, ...dateRange, limit: 1000 }),
        posAPI.listExchanges({ store_id: storeId, ...dateRange, limit: 1000 }),
        posAPI.salesSummary({ store_id: storeId, ...dateRange }),
      ]);
      
      setSales(salesRes.data || []);
      setReturns(returnsRes.data || []);
      setExchanges(exchangesRes.data || []);
      setSummary(summaryRes.data || null);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch transaction data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [storeId, search, statusFilter, dateFilter, toast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchData]);

  // Fetch store info for receipts/invoices
  const fetchStoreInfo = useCallback(async () => {
    if (!storeId) return;
    try {
      const [storeRes, brandingRes, gstSettingsRes] = await Promise.all([
        storeSyncAPI.getStoreInfo(storeId),
        businessSettingsAPI.getPublicBranding(),
        storesAPI.getGstInvoiceSettings(storeId).catch(() => ({ data: null }))
      ]);
      setStoreInfo({
        ...storeRes.data?.store,
        ...brandingRes.data
      });
      setGstInvoiceSettings(gstSettingsRes.data || null);
    } catch (error) {
      console.error('Failed to fetch store info:', error);
    }
  }, [storeId]);

  useEffect(() => {
    fetchStoreInfo();
  }, [fetchStoreInfo]);

  // Download A4 GST Invoice as PDF
  const downloadA4GSTInvoice = useCallback(() => {
    if (!a4InvoiceRef.current || !selectedItem) return;
    
    const element = a4InvoiceRef.current;
    const billNum = selectedItem.bill_number || 'BILL';
    const billDate = selectedItem.bill_datetime ? new Date(selectedItem.bill_datetime).toLocaleDateString('en-GB').replace(/\//g, '-') : new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
    const filename = `${billNum}-${billDate}.pdf`;
    
    const opt = {
      margin: 0,
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };
    
    html2pdf().set(opt).from(element).save();
    toast({ title: 'Success', description: `GST Invoice saved as ${filename}` });
  }, [selectedItem, toast]);

  // Print thermal receipt
  const handlePrintReceipt = useCallback(() => {
    if (!receiptRef.current) return;
    
    const printContent = receiptRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Receipt</title>
            <style>
              body { 
                font-family: monospace; 
                font-size: 10px; 
                margin: 0; 
                padding: 5px;
                width: 72mm;
              }
              .print-hide { display: none !important; }
              @page { size: 72mm auto; margin: 0; }
            </style>
          </head>
          <body>${printContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  }, []);

  // Reprint A4 GST Invoice via the browser's print dialog (physical print, not PDF)
  const handlePrintA4Invoice = useCallback(() => {
    if (!a4InvoiceRef.current) return;
    const printContent = a4InvoiceRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>A4 GST Invoice</title>
            <style>
              html, body { margin: 0; padding: 0; }
              body { font-family: 'Arial', 'Helvetica', sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              @page { size: A4; margin: 0; }
              @media print { .print-hide { display: none !important; } }
            </style>
          </head>
          <body>${printContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 400);
    }
  }, []);

  const openViewDialog = (item, type) => {
    setSelectedItem(item);
    setViewType(type);
    setViewDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'returned':
        return <Badge className="bg-red-100 text-red-800">Returned</Badge>;
      case 'partially_returned':
        return <Badge className="bg-amber-100 text-amber-800">Partial Return</Badge>;
      case 'void':
        return <Badge className="bg-gray-100 text-gray-800">Void</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPaymentIcon = (mode) => {
    switch (mode) {
      case 'cash':
        return <Banknote className="w-4 h-4 text-green-500" />;
      case 'card':
        return <CreditCard className="w-4 h-4 text-blue-500" />;
      case 'upi':
        return <Smartphone className="w-4 h-4 text-purple-500" />;
      default:
        return null;
    }
  };

  // Range totals come from the server (full range, same definition as HQ Sales Reports)
  const totalBills = summary?.bill_count ?? sales.length;
  const totalSales = summary?.gross_sales ?? sales.reduce((sum, s) => sum + (s.grand_total || 0), 0);
  const totalExclTax = summary?.total_excl_tax ?? 0;
  const totalTax = summary?.total_tax ?? 0;
  const totalReturns = summary?.returns_total ?? returns.reduce((sum, r) => sum + (r.total_refund || 0), 0);
  const totalExchanges = exchanges.length;
  const netSales = summary?.net_sales ?? (totalSales - totalReturns);
  const inr = (n) => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="sales-history-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Transaction History</h1>
          <p className="text-gray-500">{user?.entity_name} - View all transactions</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="border-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6" data-testid="store-sales-summary">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Bills</p>
                <p className="text-2xl font-bold text-gray-800" data-testid="store-sum-bills">{totalBills}</p>
                {summary && <p className="text-[11px] text-gray-500">{summary.items_qty} items · avg ₹{inr(summary.avg_bill_value)}</p>}
              </div>
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sales (Incl. Tax)</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="store-sum-incl">₹{inr(totalSales)}</p>
                <p className="text-[11px] text-gray-500">Excl. tax ₹{inr(totalExclTax)}</p>
              </div>
              <Receipt className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tax Collected</p>
                <p className="text-2xl font-bold text-gray-800" data-testid="store-sum-tax">₹{inr(totalTax)}</p>
                {summary && <p className="text-[11px] text-gray-500">Discount ₹{inr(summary.total_discount)}</p>}
              </div>
              <Receipt className="w-8 h-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Returns</p>
                <p className="text-2xl font-bold text-orange-600" data-testid="store-sum-returns">₹{inr(totalReturns)}</p>
                {summary && <p className="text-[11px] text-gray-500">{summary.returns_count} returns</p>}
              </div>
              <RotateCcw className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Exchanges</p>
                <p className="text-2xl font-bold text-blue-600">{totalExchanges}</p>
              </div>
              <ArrowLeftRight className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Net Sales</p>
                <p className="text-2xl font-bold text-gray-800" data-testid="store-sum-net">₹{inr(netSales)}</p>
                <p className="text-[11px] text-gray-500">Incl. tax − returns</p>
              </div>
              <History className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200 shadow-sm mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by bill number, phone, or name..."
                className="pl-10 bg-gray-50 border-gray-300"
              />
            </div>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-40 bg-gray-50 border-gray-300">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="week">Last 7 Days</SelectItem>
                <SelectItem value="month">Last 30 Days</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-gray-50 border-gray-300">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="returned">Returned</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different transaction types */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="sales" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
            <Receipt className="w-4 h-4 mr-2" />
            Sales ({sales.length})
          </TabsTrigger>
          <TabsTrigger value="returns" className="data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700">
            <RotateCcw className="w-4 h-4 mr-2" />
            Returns ({returns.length})
          </TabsTrigger>
          <TabsTrigger value="exchanges" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Exchanges ({exchanges.length})
          </TabsTrigger>
        </TabsList>

        {/* Sales Tab */}
        <TabsContent value="sales">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-emerald-500" />
                Sales Transactions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : sales.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No Sales Found</p>
                  <p className="text-sm mt-1">Try adjusting your filters</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-gray-600">Bill #</TableHead>
                        <TableHead className="text-gray-600">Time</TableHead>
                        <TableHead className="text-gray-600">Customer</TableHead>
                        <TableHead className="text-gray-600 text-center">Items</TableHead>
                        <TableHead className="text-gray-600">Payment</TableHead>
                        <TableHead className="text-gray-600 text-right">Amount</TableHead>
                        <TableHead className="text-gray-600">Status</TableHead>
                        <TableHead className="text-gray-600 text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sales.map((sale) => (
                        <TableRow key={sale.id} className="border-gray-200 hover:bg-gray-50">
                          <TableCell className="font-medium text-emerald-600">{sale.bill_number}</TableCell>
                          <TableCell className="text-gray-600">
                            <p className="text-sm">{formatDate(sale.bill_datetime)}</p>
                          </TableCell>
                          <TableCell>
                            {sale.customer_name || sale.customer_phone ? (
                              <div>
                                <p className="font-medium text-gray-800">{sale.customer_name || '-'}</p>
                                <p className="text-xs text-gray-500">{sale.customer_phone || '-'}</p>
                              </div>
                            ) : (
                              <span className="text-gray-400">Walk-in</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">{sale.items?.length || 0}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {sale.payments?.map((p, idx) => (
                                <span key={idx} title={`${p.mode}: ₹${p.amount}`}>
                                  {getPaymentIcon(p.mode)}
                                </span>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-bold text-gray-800">
                            ₹{sale.grand_total?.toLocaleString()}
                          </TableCell>
                          <TableCell>{getStatusBadge(sale.status)}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openViewDialog(sale, 'sale')}
                              className="text-gray-500 hover:text-gray-700"
                              data-testid={`view-sale-${sale.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Returns Tab */}
        <TabsContent value="returns">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-orange-500" />
                Return Transactions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : returns.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <RotateCcw className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No Returns Found</p>
                  <p className="text-sm mt-1">No returns recorded for this period</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-orange-50">
                        <TableHead className="text-gray-600">Return #</TableHead>
                        <TableHead className="text-gray-600">Original Bill</TableHead>
                        <TableHead className="text-gray-600">Date/Time</TableHead>
                        <TableHead className="text-gray-600 text-center">Items</TableHead>
                        <TableHead className="text-gray-600">Refund Mode</TableHead>
                        <TableHead className="text-gray-600 text-right">Refund Amount</TableHead>
                        <TableHead className="text-gray-600">Processed By</TableHead>
                        <TableHead className="text-gray-600 text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {returns.map((ret) => (
                        <TableRow key={ret.id} className="border-gray-200 hover:bg-orange-50/50">
                          <TableCell className="font-medium text-orange-600">{ret.return_number}</TableCell>
                          <TableCell className="text-gray-600">{ret.bill_number}</TableCell>
                          <TableCell className="text-gray-600">
                            <p className="text-sm">{formatDate(ret.return_datetime)}</p>
                          </TableCell>
                          <TableCell className="text-center">{ret.items?.length || 0}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {ret.refund_mode === 'cash' && <Banknote className="w-3 h-3 mr-1" />}
                              {ret.refund_mode || 'N/A'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-bold text-orange-600">
                            ₹{ret.total_refund?.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-gray-600">{ret.processed_by_name}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openViewDialog(ret, 'return')}
                              className="text-gray-500 hover:text-orange-600"
                              data-testid={`view-return-${ret.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exchanges Tab */}
        <TabsContent value="exchanges">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5 text-blue-500" />
                Exchange Transactions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : exchanges.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ArrowLeftRight className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No Exchanges Found</p>
                  <p className="text-sm mt-1">No exchanges recorded for this period</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-blue-50">
                        <TableHead className="text-gray-600">Exchange #</TableHead>
                        <TableHead className="text-gray-600">Original Bill</TableHead>
                        <TableHead className="text-gray-600">Date/Time</TableHead>
                        <TableHead className="text-gray-600 text-right">Return Value</TableHead>
                        <TableHead className="text-gray-600 text-right">New Value</TableHead>
                        <TableHead className="text-gray-600 text-right">Difference</TableHead>
                        <TableHead className="text-gray-600">Processed By</TableHead>
                        <TableHead className="text-gray-600 text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exchanges.map((exc) => (
                        <TableRow key={exc.id} className="border-gray-200 hover:bg-blue-50/50">
                          <TableCell className="font-medium text-blue-600">{exc.exchange_number}</TableCell>
                          <TableCell className="text-gray-600">{exc.bill_number}</TableCell>
                          <TableCell className="text-gray-600">
                            <p className="text-sm">{formatDate(exc.exchange_datetime)}</p>
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            -₹{exc.total_return_value?.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            +₹{exc.total_new_value?.toLocaleString()}
                          </TableCell>
                          <TableCell className={`text-right font-bold ${exc.price_difference >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                            {exc.price_difference >= 0 ? '+' : ''}₹{exc.price_difference?.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-gray-600">{exc.processed_by_name}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openViewDialog(exc, 'exchange')}
                              className="text-gray-500 hover:text-blue-600"
                              data-testid={`view-exchange-${exc.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Dialog - Dynamic based on type */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center justify-between">
              {viewType === 'sale' && (
                <span className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-emerald-500" />
                  Bill: {selectedItem?.bill_number}
                </span>
              )}
              {viewType === 'return' && (
                <span className="flex items-center gap-2">
                  <RotateCcw className="w-5 h-5 text-orange-500" />
                  Return: {selectedItem?.return_number}
                </span>
              )}
              {viewType === 'exchange' && (
                <span className="flex items-center gap-2">
                  <ArrowLeftRight className="w-5 h-5 text-blue-500" />
                  Exchange: {selectedItem?.exchange_number}
                </span>
              )}
              {viewType === 'sale' && selectedItem && getStatusBadge(selectedItem.status)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && viewType === 'sale' && (
            <div className="space-y-4">
              {/* Sale Info */}
              <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Date & Time</p>
                  <p className="font-medium text-gray-800">{formatDate(selectedItem.bill_datetime)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Cashier</p>
                  <p className="font-medium text-gray-800">{selectedItem.cashier_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Counter</p>
                  <p className="font-medium text-gray-800">{selectedItem.counter_id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Shift</p>
                  <p className="font-medium text-gray-800">{selectedItem.shift_id}</p>
                </div>
              </div>

              {/* Customer Info */}
              {(selectedItem.customer_name || selectedItem.customer_phone) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs text-blue-600 mb-1">Customer</p>
                  <p className="font-medium text-blue-800">{selectedItem.customer_name || '-'}</p>
                  <p className="text-sm text-blue-600">{selectedItem.customer_phone || '-'}</p>
                </div>
              )}

              {/* Items */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-gray-600">Item</TableHead>
                      <TableHead className="text-gray-600 text-center">Qty</TableHead>
                      <TableHead className="text-gray-600 text-right">Price</TableHead>
                      <TableHead className="text-gray-600 text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedItem.items?.map((item, idx) => (
                      <TableRow key={idx} className="border-gray-200">
                        <TableCell>
                          <p className="font-medium text-gray-800">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.sku}</p>
                        </TableCell>
                        <TableCell className="text-center">{item.quantity} {item.uom}</TableCell>
                        <TableCell className="text-right text-gray-600">₹{item.unit_price}</TableCell>
                        <TableCell className="text-right font-medium text-gray-800">₹{item.total_amount?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-800">₹{selectedItem.subtotal?.toFixed(2)}</span>
                </div>
                {selectedItem.total_discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-₹{selectedItem.total_discount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax (CGST + SGST)</span>
                  <span className="text-gray-800">₹{((selectedItem.total_cgst || 0) + (selectedItem.total_sgst || 0)).toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 flex justify-between">
                  <span className="font-bold text-gray-800">Grand Total</span>
                  <span className="text-xl font-bold text-emerald-600">₹{selectedItem.grand_total?.toLocaleString()}</span>
                </div>
              </div>

              {/* Payments */}
              <div className="bg-emerald-50 p-4 rounded-lg">
                <p className="text-xs text-emerald-600 mb-2">Payments</p>
                <div className="space-y-1">
                  {selectedItem.payments?.map((pay, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getPaymentIcon(pay.mode)}
                        <span className="capitalize text-emerald-800">{pay.mode}</span>
                      </div>
                      <span className="font-medium text-emerald-800">₹{pay.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selectedItem && viewType === 'return' && (
            <div className="space-y-4">
              {/* Return Info */}
              <div className="bg-orange-50 p-4 rounded-lg grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-orange-600">Original Bill</p>
                  <p className="font-medium text-orange-800">{selectedItem.bill_number}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">Date & Time</p>
                  <p className="font-medium text-orange-800">{formatDate(selectedItem.return_datetime)}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">Processed By</p>
                  <p className="font-medium text-orange-800">{selectedItem.processed_by_name}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">Refund Mode</p>
                  <p className="font-medium text-orange-800 capitalize">{selectedItem.refund_mode}</p>
                </div>
              </div>

              {/* Returned Items */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-orange-50">
                      <TableHead className="text-gray-600">Item</TableHead>
                      <TableHead className="text-gray-600 text-center">Qty</TableHead>
                      <TableHead className="text-gray-600">Reason</TableHead>
                      <TableHead className="text-gray-600 text-right">Refund</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedItem.items?.map((item, idx) => (
                      <TableRow key={idx} className="border-gray-200">
                        <TableCell>
                          <p className="font-medium text-gray-800">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.sku}</p>
                        </TableCell>
                        <TableCell className="text-center">{item.quantity}</TableCell>
                        <TableCell className="capitalize text-gray-600">{item.reason?.replace('_', ' ')}</TableCell>
                        <TableCell className="text-right font-medium text-orange-600">₹{item.refund_amount?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Refund Total */}
              <div className="bg-orange-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-orange-800">Total Refund</span>
                  <span className="text-2xl font-bold text-orange-600">₹{selectedItem.total_refund?.toLocaleString()}</span>
                </div>
              </div>

              {/* Notes */}
              {selectedItem.notes && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Notes</p>
                  <p className="text-gray-700">{selectedItem.notes}</p>
                </div>
              )}
            </div>
          )}

          {selectedItem && viewType === 'exchange' && (
            <div className="space-y-4">
              {/* Exchange Info */}
              <div className="bg-blue-50 p-4 rounded-lg grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-blue-600">Original Bill</p>
                  <p className="font-medium text-blue-800">{selectedItem.bill_number}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600">Date & Time</p>
                  <p className="font-medium text-blue-800">{formatDate(selectedItem.exchange_datetime)}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600">Processed By</p>
                  <p className="font-medium text-blue-800">{selectedItem.processed_by_name}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600">Payment Mode</p>
                  <p className="font-medium text-blue-800 capitalize">{selectedItem.payment_mode || 'N/A'}</p>
                </div>
              </div>

              {/* Exchanged Items */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-blue-50">
                      <TableHead className="text-gray-600">Returned Item</TableHead>
                      <TableHead className="text-gray-600 text-center">Qty</TableHead>
                      <TableHead className="text-gray-600 text-right">Value</TableHead>
                      <TableHead className="text-gray-600">New Item</TableHead>
                      <TableHead className="text-gray-600 text-center">Qty</TableHead>
                      <TableHead className="text-gray-600 text-right">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedItem.items?.map((item, idx) => (
                      <TableRow key={idx} className="border-gray-200">
                        <TableCell>
                          <p className="font-medium text-red-600">{item.return_product_name}</p>
                        </TableCell>
                        <TableCell className="text-center">{item.return_quantity}</TableCell>
                        <TableCell className="text-right text-red-600">-₹{item.return_value?.toFixed(2)}</TableCell>
                        <TableCell>
                          <p className="font-medium text-green-600">{item.new_product_name}</p>
                          <p className="text-xs text-gray-500">{item.new_sku}</p>
                        </TableCell>
                        <TableCell className="text-center">{item.new_quantity}</TableCell>
                        <TableCell className="text-right text-green-600">+₹{item.new_value?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Exchange Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-xs text-red-600">Return Value</p>
                  <p className="text-xl font-bold text-red-600">-₹{selectedItem.total_return_value?.toLocaleString()}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-xs text-green-600">New Value</p>
                  <p className="text-xl font-bold text-green-600">+₹{selectedItem.total_new_value?.toLocaleString()}</p>
                </div>
                <div className={`p-4 rounded-lg text-center ${selectedItem.price_difference >= 0 ? 'bg-emerald-100' : 'bg-orange-100'}`}>
                  <p className="text-xs text-gray-600">Difference</p>
                  <p className={`text-xl font-bold ${selectedItem.price_difference >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                    {selectedItem.price_difference >= 0 ? '+' : ''}₹{selectedItem.price_difference?.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Notes */}
              {selectedItem.notes && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Notes</p>
                  <p className="text-gray-700">{selectedItem.notes}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Hidden A4 GST Invoice for PDF generation */}
          {viewType === 'sale' && selectedItem && (
            <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
              <A4GSTInvoice 
                ref={a4InvoiceRef}
                sale={selectedItem}
                storeInfo={storeInfo}
                gstSettings={gstInvoiceSettings}
              />
              <ReceiptComponent
                ref={receiptRef}
                sale={selectedItem}
                storeInfo={storeInfo}
              />
            </div>
          )}
          
          <DialogFooter className="flex flex-col gap-2">
            {viewType === 'sale' && selectedItem && !(selectedItem.items?.length) && selectedItem.status !== 'cancelled' && (
              <div className="w-full p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="empty-bill-warning">
                <span>This bill has no items — it was created by a duplicate submit. Voiding removes it from sales totals and the register.</span>
                {(user?.entity_type === 'hq' || (user?.role_name || '').toLowerCase().includes('manager')) && (
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={voiding}
                    onClick={async () => {
                      setVoiding(true);
                      try {
                        await posAPI.voidEmptySale(selectedItem.id);
                        toast({ title: 'Bill voided', description: `${selectedItem.bill_number} removed from totals` });
                        setViewDialogOpen(false);
                        fetchData();
                      } catch (e) {
                        toast({ title: 'Could not void', description: e.response?.data?.detail || e.message, variant: 'destructive' });
                      } finally { setVoiding(false); }
                    }}
                    data-testid="void-empty-bill-btn"
                  >
                    Void empty bill
                  </Button>
                )}
              </div>
            )}
            {/* Reprint options for sales */}
            {viewType === 'sale' && selectedItem && (
              <div className="grid grid-cols-3 gap-2 w-full">
                <Button 
                  onClick={handlePrintReceipt}
                  variant="outline"
                  className="border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                  data-testid="reprint-receipt-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Reprint Thermal
                </Button>
                <Button 
                  onClick={handlePrintA4Invoice}
                  variant="outline"
                  className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                  data-testid="reprint-a4-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Reprint A4 Bill
                </Button>
                <Button 
                  onClick={downloadA4GSTInvoice}
                  variant="outline"
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  data-testid="download-gst-invoice-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Download A4 (PDF)
                </Button>
              </div>
            )}
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="border-gray-300">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesHistoryPage;
